import { MenuOutlined } from "@ant-design/icons";
import { Button, Drawer, Switch, Table, Typography } from "antd";
import { arrayMoveImmutable } from "array-move";
import CustomModal from "components/CustomModal";
import { useEffect, useState } from "react";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";

import { useGetAllCategoryLevel3, useUpdateBatchUpdatePosition } from "hooks/category/category";
import "./index.scss";

const DragHandle = SortableHandle(() => <MenuOutlined style={{ cursor: "grab", color: "#999" }} />);

const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);

const ConfigDrawer = ({ visible, onClose, onSave }) => {
  const [drawerSearchText, setDrawerSearchText] = useState("");
  const { data: categories, loading: loadingCategories } = useGetAllCategoryLevel3({
    levels: [3],
    isActive: true,
  });
  const { handleBatchUpdatePosition, loading: loadingUpdateBatchUpdatePosition } = useUpdateBatchUpdatePosition();

  const [drawerData, setDrawerData] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);

  // Update drawerData when drawer opens
  useEffect(() => {
    if (visible && categories && !isInitialized) {
      // Sort by position ascending
      const sortedData = [...categories].sort((a, b) => (a.position || 0) - (b.position || 0));
      setDrawerData(sortedData);
      setDrawerSearchText("");
      setIsInitialized(true);
    }
    if (!visible) {
      setIsInitialized(false);
    }
  }, [visible, categories, isInitialized]);

  const handleSaveClick = () => {
    setShowSaveConfirmModal(true);
  };

  const handleConfirmSave = async () => {
    setShowSaveConfirmModal(false);
    // Prepare data for batch update
    const items = drawerData.map((item, index) => ({
      id: item.id,
      position: index, // position starts from 0
      showOnHomeApp: item.showOnHomeApp,
    }));

    try {
      await handleBatchUpdatePosition(items);
      onSave(drawerData);
      onClose();
    } catch (error) {
      console.error("Error updating positions:", error);
    }
  };

  const handleCancelClick = () => {
    setShowCancelConfirmModal(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirmModal(false);
    onClose();
  };
  console.log(drawerData);
  const filteredDrawerData = drawerData?.filter((item) =>
    item.name?.toLowerCase().includes(drawerSearchText.toLowerCase())
  );

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const activeCount = filteredDrawerData.filter((item) => item.showOnHomeApp).length;
      const movedItem = filteredDrawerData[oldIndex];
      const movedItemWasActive = !!movedItem?.showOnHomeApp;

      let newData = arrayMoveImmutable(filteredDrawerData.slice(), oldIndex, newIndex).filter((el) => !!el);

      const movedItemIndex = newData.findIndex((item) => item.id === movedItem?.id);
      if (movedItemIndex >= 0) {
        const movedIntoActiveBlock = newIndex < activeCount;
        const movedOutOfActiveBlock = movedItemWasActive && oldIndex < activeCount && newIndex >= activeCount;
        const shouldBeActive = movedIntoActiveBlock && !movedOutOfActiveBlock;

        newData[movedItemIndex] = { ...newData[movedItemIndex], showOnHomeApp: shouldBeActive };

        const newActiveCount = newData.filter((item) => item.showOnHomeApp).length;
        if (newActiveCount > 12 && newData[12]) {
          newData[12] = { ...newData[12], showOnHomeApp: false };
        }
      }

      let finalData;
      if (drawerSearchText) {
        const filteredIds = new Set(filteredDrawerData.map((item) => item.id));
        const nonFilteredItems = drawerData.filter((item) => !filteredIds.has(item.id));
        finalData = [...newData, ...nonFilteredItems];
      } else {
        finalData = newData;
      }

      setDrawerData(finalData);

      // Auto save after drag
      const items = finalData.map((item, index) => ({
        id: item.id,
        position: index,
        showOnHomeApp: item.showOnHomeApp,
      }));
      await handleBatchUpdatePosition(items);
    }
  };

  const DraggableContainer = (props) => (
    <SortableBody
      useDragHandle
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      helperContainer={() => document.querySelector(".ant-drawer-body .ant-table-tbody")}
      {...props}
    />
  );

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    const index = filteredDrawerData?.findIndex((x) => x.id === restProps["data-row-key"]);
    return <SortableItem index={index} {...restProps} />;
  };

  const drawerColumns = [
    {
      title: "",
      dataIndex: "sort",
      key: "sort",
      width: 60,
      className: "drag-visible",
      render: () => <DragHandle />,
    },
    {
      title: "STT",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "TÊN DANH MỤC SẢN PHẨM",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "HIỂN THỊ TRÊN HOME PAGE",
      dataIndex: "showOnHomeApp",
      key: "showOnHomeApp",
      align: "center",
      width: 220,
      render: (showOnHomeApp, record) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "80%" }}>
          <Switch
            checked={showOnHomeApp}
            checkedChildren="ON"
            unCheckedChildren="OFF"
            loading={loadingUpdateBatchUpdatePosition}
            onChange={async (checked) => {
              if (checked) {
                const activeCount = drawerData.filter((item) => item.showOnHomeApp).length;
                if (activeCount >= 12) {
                  setShowWarningModal(true);
                  return;
                }

                const otherItems = drawerData.filter((item) => item.id !== record.id);
                const lastActiveIndex = otherItems.reduce((lastIndex, item, index) => {
                  return item.showOnHomeApp ? index : lastIndex;
                }, -1);

                const updatedRecord = { ...record, showOnHomeApp: true };
                let newData;

                if (lastActiveIndex === -1) {
                  newData = [updatedRecord, ...otherItems];
                } else {
                  newData = [
                    ...otherItems.slice(0, lastActiveIndex + 1),
                    updatedRecord,
                    ...otherItems.slice(lastActiveIndex + 1),
                  ];
                }

                setDrawerData(newData);

                const items = newData.map((item, index) => ({
                  id: item.id,
                  position: index,
                  showOnHomeApp: item.showOnHomeApp,
                }));
                await handleBatchUpdatePosition(items);
              } else {
                const currentIndex = drawerData.findIndex((item) => item.id === record.id);
                if (currentIndex === -1) return;

                const activeItems = drawerData.filter((item) => item.showOnHomeApp && item.id !== record.id);
                const currentRecord = { ...record, showOnHomeApp: false };

                if (currentIndex < 12) {
                  const newData = [...drawerData];
                  newData[currentIndex] = currentRecord;

                  const reorderedData = [
                    ...activeItems.slice(0, 11).map((item) => ({ ...item, showOnHomeApp: true })),
                    currentRecord,
                    ...drawerData.filter((item) => item.id !== record.id && !item.showOnHomeApp),
                  ];

                  setDrawerData(reorderedData);

                  const items = reorderedData.map((item, index) => ({
                    id: item.id,
                    position: index,
                    showOnHomeApp: item.showOnHomeApp,
                  }));
                  await handleBatchUpdatePosition(items);
                } else {
                  const newData = drawerData.map((item) =>
                    item.id === record.id ? { ...item, showOnHomeApp: checked } : item
                  );
                  setDrawerData(newData);

                  const items = newData.map((item, index) => ({
                    id: item.id,
                    position: index,
                    showOnHomeApp: item.showOnHomeApp,
                  }));
                  await handleBatchUpdatePosition(items);
                }
              }
            }}
          />
        </div>
      ),
    },
    {
      title: "THAO TÁC",
      key: "action",
      width: 180,
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "75%" }}>
          <Button
            type="primary"
            ghost
            size="small"
            icon={<span style={{ marginRight: "4px" }}>↑</span>}
            onClick={async () => {
              const newData = drawerData.filter((item) => item.id !== record.id);
              const updatedRecord = { ...record, showOnHomeApp: true };

              const activeItems = newData.filter((item) => item.showOnHomeApp);

              let finalData;
              if (activeItems.length >= 12) {
                const updatedData = newData.map((item, index) => {
                  const activeBeforeThis = newData.slice(0, index + 1).filter((i) => i.showOnHomeApp).length;
                  if (item.showOnHomeApp && activeBeforeThis === 12) {
                    return { ...item, showOnHomeApp: false };
                  }
                  return item;
                });
                finalData = [updatedRecord, ...updatedData];
              } else {
                finalData = [updatedRecord, ...newData];
              }

              setDrawerData(finalData);

              // Auto save after moving to top
              const items = finalData.map((item, index) => ({
                id: item.id,
                position: index,
                showOnHomeApp: item.showOnHomeApp,
              }));
              await handleBatchUpdatePosition(items);
            }}
            style={{
              borderColor: "#1890ff",
              color: "#1890ff",
            }}
          >
            Ưu tiên cao nhất
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginRight: "40px" }}>
          <Typography.Text style={{ fontSize: "22px" }} strong>
            Cấu hình danh mục sản phẩm
          </Typography.Text>
          {/* <div style={{ display: "flex", gap: "8px" }}>
            <Button onClick={handleCancelClick}>Hủy</Button>
            <Button type="primary" onClick={handleSaveClick} loading={loadingUpdateBatchUpdatePosition}>
              Lưu
            </Button>
          </div> */}
        </div>
      }
      placement="right"
      onClose={onClose}
      visible={visible}
      width={1000}
      closable={true}
      className="config-drawer"
    >
      <div>
        {/* <Input
          prefix={<FontAwesomeIcon icon={Icon.faSearch} />}
          placeholder="Tìm kiếm theo tên danh mục sản phẩm"
          allowClear
          value={drawerSearchText}
          onChange={(e) => setDrawerSearchText(e.target.value)}
          style={{ marginBottom: "16px" }}
        /> */}

        <div style={{ color: "#FE3F3FE0", fontSize: "12px", padding: "0 0 24px 0" }}>
          *GIỚI HẠN HIỂN THỊ HOMEPAGE (TỐI ĐA 12 DANH MỤC Ở TRẠNG THÁI ACTIVE)
        </div>

        <Table
          pagination={false}
          dataSource={filteredDrawerData}
          columns={drawerColumns}
          rowKey="id"
          loading={loadingCategories}
          scroll={{ y: 600 }}
          components={{
            body: {
              wrapper: DraggableContainer,
              row: DraggableBodyRow,
            },
          }}
        />
      </div>

      <CustomModal
        selfVisibleControlled={false}
        footer={false}
        visible={showWarningModal}
        onCancel={() => setShowWarningModal(false)}
        onOke={() => setShowWarningModal(false)}
        title={
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            Chỉ được phép tối đa 12 bản ghi active
          </Typography.Title>
        }
        message="Bạn đã đạt giới hạn 12 bản ghi đang hoạt động. Vui lòng tắt một bản ghi hiện có trước khi kích hoạt bản ghi mới."
        customIcon={
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "#FF9F43",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <span style={{ fontSize: "32px", color: "white", fontWeight: "bold" }}>!</span>
          </div>
        }
        hideConfirmButton={true}
        hideCancelButton={true}
        centered
      />

      <CustomModal
        selfVisibleControlled={false}
        footer={false}
        visible={showSaveConfirmModal}
        onCancel={() => setShowSaveConfirmModal(false)}
        onOke={handleConfirmSave}
        title={
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            Lưu thay đổi
          </Typography.Title>
        }
        message="Sau khi lưu, danh mục hiển thị trên trang chủ sẽ được cập nhật theo thứ tự mới."
        customIcon={
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "#28C76F",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <span style={{ fontSize: "32px", color: "white", fontWeight: "bold" }}>✓</span>
          </div>
        }
        cancelButtonLabel="Hủy"
        okeButtonLabel="Lưu thay đổi"
        buttonLoading={loadingUpdateBatchUpdatePosition}
        centered
      />

      <CustomModal
        selfVisibleControlled={false}
        footer={false}
        visible={showCancelConfirmModal}
        onCancel={() => setShowCancelConfirmModal(false)}
        onOke={handleConfirmCancel}
        title={
          <Typography.Title level={4} style={{ marginBottom: 0 }}>
            Hủy thay đổi
          </Typography.Title>
        }
        message="Bạn vừa thay đổi thứ tự danh mục, nếu hủy bây giờ thì các thay đổi này sẽ không được lưu."
        customIcon={
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              background: "#FF9F43",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            <span style={{ fontSize: "32px", color: "white", fontWeight: "bold" }}>!</span>
          </div>
        }
        cancelButtonLabel="Hủy"
        okeButtonLabel="Hủy thay đổi"
        centered
      />
    </Drawer>
  );
};

export default ConfigDrawer;
